import React, { useState } from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import ExpandableSection from '@amzn/awsui-components-core/expandable-section';
import SegmentedControl from '@amzn/awsui-components-core/segmented-control';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import type { StarRocksQuery } from 'src/components/star-grid/api/reportEngineTypes';
import { QuerySqlBlock, type SqlForm } from 'src/components/star-grid/components/QuerySqlBlock';
import { ReportQueryAnalysis } from 'src/components/star-grid/components/ReportQueryAnalysis';
import { useQueryAnalysis } from 'src/components/star-grid/hooks/useQueryAnalysis';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { formatDurationMs } from 'src/components/star-grid/utils/reportStatus';

/**
 * Every StarRocks query the last load ran, slowest expanded: as it ran (values bound) or as the code
 * wrote it, each copyable; and, on request, StarRocks' plan for the report's fact scans.
 */
export const ReportStatsQueries: React.FC<{
  queries: readonly StarRocksQuery[];
  onExplain?: () => Promise<readonly string[]>;
  /** The report's fact scan, whose stored profile the analysis reads. */
  queryId?: string | null;
}> = ({ queries, onExplain, queryId }) => {
  const text = useStarGridText();
  const [form, setForm] = useState<SqlForm>('run');
  const analysis = useQueryAnalysis(onExplain, queryId);
  const slowest = Math.max(0, ...queries.map((query) => query.ms));
  return (
    <SpaceBetween size="s">
      <SpaceBetween direction="horizontal" size="s">
        <SegmentedControl
          selectedId={form}
          onChange={({ detail }) => setForm(detail.selectedId as SqlForm)}
          options={[
            { id: 'run', text: text('statsSqlAsRun') },
            { id: 'code', text: text('statsSqlAsCode') }
          ]}
        />
        {(onExplain || queryId) && (
          <Button onClick={analysis.load} loading={analysis.loading} iconName="search">
            {text('statsAnalyze')}
          </Button>
        )}
      </SpaceBetween>
      {analysis.error && <Alert type="error">{analysis.error}</Alert>}
      <ReportQueryAnalysis analysis={analysis.analysis} plans={analysis.plans} />
      {queries.length === 0 && <Box color="text-status-inactive">{text('statsNoQueries')}</Box>}
      {queries.map((query, index) => (
        <ExpandableSection
          // Queries have no id of their own; their order is the order the request ran them.
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          defaultExpanded={query.ms === slowest}
          headerText={`#${index + 1} · ${formatDurationMs(query.ms)} · ${query.rows} ${text('statsRows')}${
            query.error ? ` · ${text('statsFailed')}` : ''
          }`}
        >
          <QuerySqlBlock sql={query.sql} template={query.template} form={form} />
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
};
